import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import parse, { DOMNode, Element, domToReact } from 'html-react-parser';

import { ArtifactBlock } from './artifact-block';
import { EndBlock } from './end-block';
import { REF_PLACEHOLDER_TAG, formatContent } from './format-content';
import { KimChronologySection } from './kim-chronology-section';
import { KimDocumentInconsistenciesBlock } from './kim-document-inconsistencies-block';
import { KimInconsistencyBlock } from './kim-inconsistency-block';
import { KimTable } from './kim-table';
import { Reference } from './reference';
import { CLICKABLE_REF_TYPES } from './reference-styles';
import { UserMessage } from './user-message';
import {
  ArtifactBlock as ArtifactBlockType,
  ArtifactType,
  ContentBlock,
  DocumentInconsistenciesBlock as DocumentInconsistenciesBlockType,
  InconsistencyBlock as InconsistencyBlockType,
  KimMessageType,
  ReferenceType,
} from '../../../../../../../../../../../backend/src/types/types';
import { renderKimMarkdown } from '../../utils/kim-markdown';
import { KimThreadFile } from '@/api/types/backend-dto-types';
import { ReferencePreviewPopover } from '@/new/components/reference-preview-popover';
import { cn } from '@/new/lib/utils';

export { REF_PLACEHOLDER_TAG, formatContent } from './format-content';

export function getReferenceReplacer(
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>,
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void,
  options?: { hideTableActions?: boolean },
) {
  const replacer = (domNode: DOMNode) => {
    if (domNode instanceof Element && domNode.name === 'table') {
      return (
        <KimTable hideActions={options?.hideTableActions}>
          {domToReact(domNode.children as DOMNode[], { replace: replacer })}
        </KimTable>
      );
    }
    if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
      const id = domNode.attribs['data-id'];
      const refType = domNode.attribs['data-ref-type'] as ReferenceType;
      const reference = (
        <Reference
          index={referenceIndexMap.get(id)?.index ?? 0}
          id={id}
          referenceType={refType}
          onClick={CLICKABLE_REF_TYPES.has(refType) ? onReferenceClick : undefined}
        />
      );
      if (CLICKABLE_REF_TYPES.has(refType)) {
        return (
          <ReferencePreviewPopover id={id} referenceType={refType}>
            {reference}
          </ReferencePreviewPopover>
        );
      }
      return reference;
    }
  };
  return replacer;
}

// Markdown -> HTML -> React parsing is by far the most expensive step (a large table can be
// thousands of elements), so it lives in its own memoized component keyed only on the blocks
// and reference indices. The click handler is routed through a ref so callback identity
// changes can never invalidate the parse.
const MessageTextSegment = memo(function MessageTextSegment({
  blocks,
  referenceIndexMap,
  onReferenceClick,
  type,
  isFirst,
}: {
  blocks: ContentBlock[];
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  type: KimMessageType;
  isFirst: boolean;
}) {
  const onReferenceClickRef = useRef(onReferenceClick);
  useEffect(() => {
    onReferenceClickRef.current = onReferenceClick;
  });
  const stableOnReferenceClick = useCallback(
    (id: string, referenceType: ReferenceType) => onReferenceClickRef.current?.(id, referenceType),
    [],
  );

  const hasReferenceClick = Boolean(onReferenceClick);
  const parsed = useMemo(() => {
    const combined = formatContent(blocks);
    const rawHtml = renderKimMarkdown(combined);
    const html = rawHtml.replace(new RegExp(`(<\\/${REF_PLACEHOLDER_TAG}>)\\s+(<${REF_PLACEHOLDER_TAG})`, 'g'), '$1$2');
    return parse(html, {
      replace: getReferenceReplacer(referenceIndexMap, hasReferenceClick ? stableOnReferenceClick : undefined),
    });
  }, [blocks, referenceIndexMap, hasReferenceClick, stableOnReferenceClick]);

  return (
    <div className={cn(type === KimMessageType.Ai && 'px-4', 'mx-auto flex max-w-3xl items-start gap-3.25')}>
      {isFirst && type === KimMessageType.AiReasoning && (
        <div className="mt-2.25 size-1.25 shrink-0 rounded-full bg-gray-300" />
      )}
      <div
        className={cn(
          'prose prose-sm prose-headings:my-3 prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-hr:my-6 prose-p:leading-normal prose-p:text-gray-900 prose-headings:font-semibold max-w-none min-w-0 flex-1',
          type === KimMessageType.AiReasoning && 'prose-p:text-gray-600 italic',
        )}
      >
        {parsed}
      </div>
    </div>
  );
});

export const KimMessage = memo(function KimMessage({
  messageId,
  type,
  content,
  isComplete,
  referenceIndexMap,
  onReferenceClick,
  onDownloadClick,
  artifactsById,
  latestArtifactId,
  isDownloading,
  createdAt,
  files,
  isOwnThread,
}: {
  messageId?: string;
  type: KimMessageType;
  content: ContentBlock[];
  isComplete: boolean;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
  /** Receives this message's id so one stable parent callback can serve every message. */
  onDownloadClick?: (messageId?: string, artifactId?: string) => void;
  artifactsById?: Map<string, ArtifactBlockType & { published: boolean | undefined }>;
  latestArtifactId?: string | null;
  isDownloading?: boolean;
  createdAt?: string;
  files?: KimThreadFile[];
  isOwnThread?: boolean;
}) {
  const isUser = type === KimMessageType.User;

  const hasChronology = useMemo(
    () => content.some((block) => block.type === 'artifact' && block.artifactType === ArtifactType.Chronology),
    [content],
  );

  const { factIds, chunkIds, directDocIds } = useMemo(() => {
    const factIds = new Set<string>();
    const chunkIds = new Set<string>();
    const directDocIds = new Set<string>();

    const collect = (refType: ReferenceType, id: string) => {
      if (refType === ReferenceType.Fact) factIds.add(id);
      else if (refType === ReferenceType.Chunk) chunkIds.add(id);
      else if (refType === ReferenceType.Document) directDocIds.add(id);
    };

    const visit = (block: ContentBlock) => {
      if (block.type === 'reference') {
        collect(block.refType, block.id);
      }
      if (block.type === 'artifact') {
        block.content.forEach((artifact) => {
          if (artifact.type === 'reference') {
            collect(artifact.refType, artifact.id);
          }
        });
      }
      if (block.type === 'inconsistency') {
        block.description?.forEach(visit);
        block.references?.forEach((incRef) => collect(incRef.reference.refType, incRef.reference.id));
      }
      if (block.type === 'document_inconsistencies') {
        block.content?.forEach(visit);
      }
    };

    for (const block of content) visit(block);
    return {
      factIds: Array.from(factIds),
      chunkIds: Array.from(chunkIds),
      directDocIds: Array.from(directDocIds),
    };
  }, [content]);

  // Used when feedback is submitted
  const answerSignals = useMemo(() => {
    const artifactTypes = new Set<ArtifactType>();
    let threadFileChunkRefCount = 0;
    let textForAnalysis = '';

    for (const block of content) {
      if (block.type === 'text') {
        textForAnalysis += `\n${block.value}`;
      } else if (block.type === 'reference') {
        if (block.refType === ReferenceType.ThreadFileChunk) threadFileChunkRefCount += 1;
      } else if (block.type === 'artifact') {
        artifactTypes.add(block.artifactType);
        for (const inner of block.content) {
          if (inner.type === 'reference' && inner.refType === ReferenceType.ThreadFileChunk) {
            threadFileChunkRefCount += 1;
          } else if (inner.type === 'text' && block.artifactType === ArtifactType.Draft) {
            textForAnalysis += `\n${inner.value}`;
          }
        }
      }
    }

    const hasTable = /\n\s*\|[\s\-:|]+\|\s*\n/.test(textForAnalysis);
    const wordCount = textForAnalysis.trim().length === 0 ? 0 : textForAnalysis.trim().split(/\s+/).length;

    return {
      hasTable,
      artifactTypes: Array.from(artifactTypes),
      wordCount,
      threadFileChunkRefCount,
    };
  }, [content]);

  // Cheap grouping only — the expensive markdown parse happens per text segment
  // inside the memoized MessageTextSegment below.
  const segments = useMemo(() => {
    type TextSegment = { type: 'text'; blocks: ContentBlock[] };
    type ArtifactSegment = { type: 'artifact'; id: string; artifact: ArtifactBlockType };
    type ChronologySegment = { type: 'chronology'; factIds: string[] };
    type InconsistencySegment = { type: 'inconsistency'; block: InconsistencyBlockType };
    type DocumentInconsistenciesSegment = {
      type: 'document_inconsistencies';
      block: DocumentInconsistenciesBlockType;
    };
    type Segment =
      | TextSegment
      | ArtifactSegment
      | ChronologySegment
      | InconsistencySegment
      | DocumentInconsistenciesSegment;

    const result: Segment[] = [];
    let textBlocks: ContentBlock[] = [];

    const flushText = () => {
      if (textBlocks.length > 0) {
        result.push({ type: 'text', blocks: textBlocks });
        textBlocks = [];
      }
    };

    for (const block of content) {
      if (block.type === 'artifact' && block.artifactType === ArtifactType.Chronology) {
        flushText();
        const factIds = block.content
          .filter(
            (c): c is Extract<typeof c, { type: 'reference' }> =>
              c.type === 'reference' && c.refType === ReferenceType.Fact,
          )
          .map((c) => c.id);
        if (factIds.length > 0) {
          result.push({ type: 'chronology', factIds });
        }
      } else if (block.type === 'artifact') {
        flushText();
        const id = `${block.id}-${block.versionNumber}`;
        const artifact = artifactsById?.get(id);
        if (artifact) result.push({ type: 'artifact', id, artifact });
      } else if (block.type === 'inconsistency') {
        flushText();
        result.push({ type: 'inconsistency', block });
      } else if (block.type === 'document_inconsistencies') {
        flushText();
        result.push({ type: 'document_inconsistencies', block });
      } else if (block.type === 'survey') {
        // Survey blocks are rendered separately near the input, not inline.
        continue;
      } else {
        textBlocks.push(block);
      }
    }

    flushText();

    return result;
  }, [content, artifactsById]);

  const renderedSegments = segments.map((segment, i) => {
    if (segment.type === 'chronology') {
      return (
        <KimChronologySection
          key={`chron-${i}`}
          factIds={segment.factIds}
          referenceIndexMap={referenceIndexMap}
          onReferenceClick={onReferenceClick}
        />
      );
    }

    if (segment.type === 'inconsistency') {
      return (
        <KimInconsistencyBlock
          key={`inc-${segment.block.id}`}
          block={segment.block}
          referenceIndexMap={referenceIndexMap}
          onReferenceClick={onReferenceClick}
        />
      );
    }

    if (segment.type === 'document_inconsistencies') {
      return <KimDocumentInconsistenciesBlock key={`doc-inc-${segment.block.id}`} block={segment.block} />;
    }

    if (segment.type === 'artifact') {
      return (
        <ArtifactBlock
          key={segment.id}
          id={segment.id}
          content={segment.artifact.content}
          referenceIndexMap={referenceIndexMap}
          onReferenceClick={onReferenceClick}
          onDownloadClick={onDownloadClick ? () => onDownloadClick(messageId, segment.id) : undefined}
          collapsed={segment.id !== latestArtifactId}
          version={segment.artifact.versionNumber}
          isDownloading={isDownloading}
          artifactsById={artifactsById}
          isOwnThread={isOwnThread}
        />
      );
    }

    return (
      <MessageTextSegment
        key={i}
        blocks={segment.blocks}
        referenceIndexMap={referenceIndexMap}
        onReferenceClick={onReferenceClick}
        type={type}
        isFirst={i === 0}
      />
    );
  });

  const buildMarkdownToCopy = () =>
    content
      .map((block) => {
        if (block.type === 'text') return block.value;
        if (block.type === 'artifact' && block.artifactType === ArtifactType.Draft) {
          const draftText = block.content.map((c) => (c.type === 'text' ? c.value : '')).join('');
          return `\n\n${draftText}\n\n`;
        }
        return '';
      })
      .join('')
      .replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, '');

  const getTextToCopy = () => buildMarkdownToCopy();
  const getHtmlToCopy = () => renderKimMarkdown(buildMarkdownToCopy());

  if (isUser) {
    return (
      <UserMessage
        content={content}
        createdAt={createdAt}
        files={files}
        onFileClick={
          onReferenceClick ? (fileId: string) => onReferenceClick(fileId, ReferenceType.ThreadFile) : undefined
        }
      />
    );
  }

  if (renderedSegments.length === 0) {
    return null;
  }

  return (
    <div className="flex">
      <div
        className={cn(
          // the [font-feature-settings:\'calt\'_0] disables converting (1) into a weird unicode char
          "w-full min-w-0 rounded-xl py-2 [font-feature-settings:'calt'_0]",
        )}
      >
        <div className="font-serif">{renderedSegments}</div>

        {type === KimMessageType.Ai && isComplete && (
          <EndBlock
            messageId={messageId}
            getTextToCopy={getTextToCopy}
            getHtmlToCopy={getHtmlToCopy}
            factIds={factIds}
            chunkIds={chunkIds}
            directDocIds={directDocIds}
            referenceIndexMap={referenceIndexMap}
            onReferenceClick={onReferenceClick}
            onDownloadClick={onDownloadClick ? () => onDownloadClick(messageId) : undefined}
            isDownloading={isDownloading}
            isChronology={hasChronology}
            hasTable={answerSignals.hasTable}
            artifactTypes={answerSignals.artifactTypes}
            wordCount={answerSignals.wordCount}
            threadFileChunkRefCount={answerSignals.threadFileChunkRefCount}
          />
        )}
      </div>
    </div>
  );
});
